import React, { useState, useEffect } from 'react';
import { MessageSquare, TrendingUp, TrendingDown, BarChart3, Newspaper, Users, Briefcase } from 'lucide-react';
import api from '../../utils/apiClient';

const StockSentimentSection = ({ symbol }) => {
  const [sentimentData, setSentimentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (symbol) {
      fetchSentimentData();
    }
  }, [symbol]);

  const fetchSentimentData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/stocks/sentiment/${symbol}`);
      if (response.data.success) {
        // Handle both response structures: { success: true, data: {...} } or { success: true, ...data }
        const sentiment = response.data.data || response.data;
        setSentimentData(sentiment);
      } else {
        setError('Failed to fetch sentiment data');
      }
    } catch (err) {
      console.error('Error fetching sentiment data:', err);
      const body = err.response?.data;
      setError(
        body?.message ||
          body?.error ||
          err.message ||
          'Failed to load sentiment data'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Stock Sentiment</h3>
        <p className="text-sm text-amber-800">{error}</p>
      </div>
    );
  }

  if (!sentimentData) {
    return null;
  }

  const getSentimentColor = (score) => {
    if (score >= 70) return 'text-green-600 bg-green-50';
    if (score >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getSentimentLabel = (score) => {
    if (score >= 70) return 'BULLISH';
    if (score >= 50) return 'NEUTRAL';
    return 'BEARISH';
  };

  // Handle different response structures from backend
  const overallScore =
    sentimentData.overallSentiment ?? sentimentData.overallScore ?? sentimentData.sentimentScore ?? null;

  if (overallScore == null || Number.isNaN(Number(overallScore))) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Stock Sentiment</h3>
        <p className="text-sm text-amber-800">Sentiment data did not include a usable score.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-6">
        <MessageSquare className="h-6 w-6 text-blue-600 mr-2" />
        <h3 className="text-xl font-bold text-gray-900">Stock Sentiment</h3>
      </div>

      {/* Overall Sentiment Score */}
      <div className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700">Overall Sentiment Score</h4>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${getSentimentColor(overallScore)}`}>
            {getSentimentLabel(overallScore)}
          </span>
        </div>
        <div className="flex items-end mb-2">
          <span className="text-5xl font-bold text-blue-900 mr-2">{overallScore}</span>
          <span className="text-2xl text-gray-600 mb-2">/100</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full ${
              overallScore >= 70 ? 'bg-green-500' :
              overallScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${overallScore}%` }}
          />
        </div>
        <p className="text-xs text-gray-600 mt-2">
          News uses live headlines and sentiment signals. Social and analyst metrics use Finnhub when configured on the server.
        </p>
      </div>

      {/* Sentiment Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* News Sentiment */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center mb-2">
            <Newspaper className="h-5 w-5 text-blue-600 mr-2" />
            <h5 className="text-sm font-semibold text-gray-900">News</h5>
          </div>
          <div className="mb-2">
            <span className="text-2xl font-bold text-gray-900">
              {(() => {
                // Safely extract news sentiment score - handle both number and object
                let newsScore = 0;
                if (sentimentData.newsSentiment) {
                  if (typeof sentimentData.newsSentiment === 'number') {
                    newsScore = sentimentData.newsSentiment;
                  } else if (sentimentData.newsSentiment.score !== undefined) {
                    newsScore = sentimentData.newsSentiment.score;
                  }
                } else if (sentimentData.breakdown?.news !== undefined) {
                  newsScore = sentimentData.breakdown.news;
                }
                return newsScore;
              })()}%
            </span>
            <span className="text-sm text-gray-600 ml-1">
              {(() => {
                let newsScore = 0;
                if (sentimentData.newsSentiment) {
                  if (typeof sentimentData.newsSentiment === 'number') {
                    newsScore = sentimentData.newsSentiment;
                  } else if (sentimentData.newsSentiment.score !== undefined) {
                    newsScore = sentimentData.newsSentiment.score;
                  }
                } else if (sentimentData.breakdown?.news !== undefined) {
                  newsScore = sentimentData.breakdown.news;
                }
                return newsScore >= 50 ? 'Bullish' : 'Bearish';
              })()}
            </span>
          </div>
          {(() => {
            const articlesCount = sentimentData.newsSentiment?.articlesAnalyzed || sentimentData.newsCount;
            return articlesCount && typeof articlesCount === 'number' ? (
              <p className="text-xs text-gray-500">
                {articlesCount} articles analyzed
              </p>
            ) : null;
          })()}
        </div>

        {/* Social Media Sentiment */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center mb-2">
            <Users className="h-5 w-5 text-purple-600 mr-2" />
            <h5 className="text-sm font-semibold text-gray-900">Social Media</h5>
          </div>
          <div className="mb-2">
            <span className="text-2xl font-bold text-gray-900">
              {(() => {
                // Safely extract social sentiment score
                let socialScore = 0;
                if (sentimentData.socialSentiment) {
                  if (typeof sentimentData.socialSentiment === 'number') {
                    socialScore = sentimentData.socialSentiment;
                  } else if (sentimentData.socialSentiment.score !== undefined) {
                    socialScore = sentimentData.socialSentiment.score;
                  }
                } else if (sentimentData.breakdown?.social !== undefined) {
                  socialScore = sentimentData.breakdown.social;
                }
                return socialScore;
              })()}%
            </span>
            <span className="text-sm text-gray-600 ml-1">
              {(() => {
                let socialScore = 0;
                if (sentimentData.socialSentiment) {
                  if (typeof sentimentData.socialSentiment === 'number') {
                    socialScore = sentimentData.socialSentiment;
                  } else if (sentimentData.socialSentiment.score !== undefined) {
                    socialScore = sentimentData.socialSentiment.score;
                  }
                } else if (sentimentData.breakdown?.social !== undefined) {
                  socialScore = sentimentData.breakdown.social;
                }
                return socialScore >= 50 ? 'Bullish' : 'Bearish';
              })()}
            </span>
          </div>
          {(() => {
            const mentions = sentimentData.socialSentiment?.mentions || sentimentData.socialMentions;
            return mentions && typeof mentions === 'number' ? (
              <p className="text-xs text-gray-500">
                {mentions.toLocaleString()} mentions
              </p>
            ) : null;
          })()}
          {sentimentData.socialSentiment?.dataNote && (
            <p className="text-xs text-amber-700 mt-2">{sentimentData.socialSentiment.dataNote}</p>
          )}
        </div>

        {/* Analyst Sentiment */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center mb-2">
            <Briefcase className="h-5 w-5 text-green-600 mr-2" />
            <h5 className="text-sm font-semibold text-gray-900">Analysts</h5>
          </div>
          <div className="mb-2">
            <span className="text-2xl font-bold text-gray-900">
              {(() => {
                // Extract score properly - analystSentiment is an object with buy/hold/sell, calculate score from that
                let analystScore = 50; // Default neutral
                if (sentimentData.analystSentiment) {
                  if (typeof sentimentData.analystSentiment === 'number') {
                    analystScore = sentimentData.analystSentiment;
                  } else if (sentimentData.analystSentiment.score) {
                    analystScore = sentimentData.analystSentiment.score;
                  } else if (sentimentData.analystSentiment.buy !== undefined) {
                    // Calculate score from buy/hold/sell percentages
                    const buy = sentimentData.analystSentiment.buy || 0;
                    const hold = sentimentData.analystSentiment.hold || 0;
                    const sell = sentimentData.analystSentiment.sell || 0;
                    const total = buy + hold + sell;
                    if (total > 0) {
                      analystScore = ((buy / total) * 100) + ((hold / total) * 50);
                    }
                  }
                } else if (sentimentData.breakdown?.analysts) {
                  analystScore = sentimentData.breakdown.analysts;
                }
                return Math.round(analystScore);
              })()}%
            </span>
            <span className="text-sm text-gray-600 ml-1">
              {(() => {
                let analystScore = 50;
                if (sentimentData.analystSentiment) {
                  if (typeof sentimentData.analystSentiment === 'number') {
                    analystScore = sentimentData.analystSentiment;
                  } else if (sentimentData.analystSentiment.score) {
                    analystScore = sentimentData.analystSentiment.score;
                  } else if (sentimentData.analystSentiment.buy !== undefined) {
                    const buy = sentimentData.analystSentiment.buy || 0;
                    const hold = sentimentData.analystSentiment.hold || 0;
                    const sell = sentimentData.analystSentiment.sell || 0;
                    const total = buy + hold + sell;
                    if (total > 0) {
                      analystScore = ((buy / total) * 100) + ((hold / total) * 50);
                    }
                  }
                } else if (sentimentData.breakdown?.analysts) {
                  analystScore = sentimentData.breakdown.analysts;
                }
                return analystScore >= 50 ? 'Bullish' : 'Bearish';
              })()}
            </span>
          </div>
          {sentimentData.analystSentiment && (
            <p className="text-xs text-gray-500">
              {(() => {
                const buy = sentimentData.analystSentiment?.buy || 0;
                const hold = sentimentData.analystSentiment?.hold || 0;
                const sell = sentimentData.analystSentiment?.sell || 0;
                const total = buy + hold + sell;
                return total > 0 ? `${total} ratings (${buy} Buy, ${hold} Hold, ${sell} Sell)` : 
                       (sentimentData.analystRatings ? `${sentimentData.analystRatings} ratings` : 'No ratings');
              })()}
            </p>
          )}
          {sentimentData.analystSentiment?.dataNote && (
            <p className="text-xs text-amber-700 mt-2">{sentimentData.analystSentiment.dataNote}</p>
          )}
        </div>
      </div>

      {/* Recent News Headlines */}
      {(() => {
        const headlines = sentimentData.newsSentiment?.headlines || sentimentData.recentNews;
        if (headlines && Array.isArray(headlines) && headlines.length > 0) {
          return (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Recent News Headlines</h4>
              <div className="space-y-2">
                {headlines.slice(0, 5).map((news, index) => {
                  // Ensure news is an object with headline/title
                  if (!news || typeof news !== 'object') return null;
                  const headline = news.headline || news.title || String(news);
                  return (
                    <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200">
                      <p className="text-sm text-gray-900">{headline}</p>
                      {news.source && typeof news.source === 'string' && (
                        <p className="text-xs text-gray-500 mt-1">Source: {news.source}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

export default StockSentimentSection;

